import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getDate } from '@einhaus/meteoric-helpers';
import { trpc } from '@/api/trpc.ts';
import { useAppQuery } from '@/hooks/useAppQuery';
import StrategyColorDot from '@/pages/User/PortfolioPerformance/components/StrategyColorDot';
import {
    formatSignedCurrency,
    formatSignedPercent,
    formatUnsignedCurrency,
    signedToneClass
} from '@/pages/User/PortfolioPerformance/performanceFormat';
import {
    assignStrategyColors,
    buildGlanceStats,
    buildStrategyRows,
    formatShortDate
} from '@/pages/User/PortfolioPerformance/performanceUtils';

interface PerformanceGlanceCardProps {
    portfolioId: number;
}

const SPARKLINE_DAYS = 30;

const Sparkline = ({ values, positive }: { values: number[]; positive: boolean }) => {
    const width = 420;
    const height = 90;
    const low = Math.min(...values);
    const high = Math.max(...values);
    const span = high - low || 1;
    const x = (index: number) => 4 + ((width - 8) * index) / Math.max(1, values.length - 1);
    const y = (value: number) => height - 6 - ((height - 14) * (value - low)) / span;
    const path = values.map((value, index) => `${index ? 'L' : 'M'}${x(index).toFixed(1)} ${y(value).toFixed(1)}`).join(' ');
    const color = positive ? '#29a740' : '#ef4444';

    return (
        <svg aria-hidden="true" className="block w-full" preserveAspectRatio="none" viewBox={`0 0 ${width} ${height}`}>
            <defs>
                <linearGradient id="glance-sparkline-fill" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0" stopColor={color} stopOpacity="0.3" />
                    <stop offset="1" stopColor={color} stopOpacity="0" />
                </linearGradient>
            </defs>
            <path d={`${path} V${height} H4 Z`} fill="url(#glance-sparkline-fill)" />
            <path d={path} fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke" />
        </svg>
    );
};

/**
 * Compact performance summary for the portfolio page: a 30-day equity sparkline, period P&L figures and the
 * strategies contributing most, all pointing to the full Performance page.
 */
const PerformanceGlanceCard: React.FC<PerformanceGlanceCardProps> = ({ portfolioId }) => {
    const today = useMemo(() => getDate({ format: 'ymd', timezone: 'America/New_York' }), []);

    const breakdownQuery = useAppQuery(
        {
            ...trpc.user.fetchPortfolioPerformanceBreakdown.queryOptions({ id: portfolioId }),
            enabled: portfolioId > 0,
            staleTime: 60_000
        },
        { fallbackErrorMessage: 'Failed to load the performance summary', showErrorToast: false }
    );

    const breakdown = breakdownQuery.data ?? null;
    const days = useMemo(() => breakdown?.days ?? [], [breakdown]);
    const trades = useMemo(() => breakdown?.trades ?? [], [breakdown]);
    const strategies = useMemo(() => breakdown?.strategies ?? [], [breakdown]);

    const sparklineDays = useMemo(() => days.slice(-SPARKLINE_DAYS), [days]);
    const glanceStats = useMemo(() => buildGlanceStats(days, today), [days, today]);
    const strategyColors = useMemo(() => assignStrategyColors(strategies), [strategies]);

    const contributors = useMemo(
        () =>
            buildStrategyRows({ strategies, trades, days, periodStart: null, today })
                .filter((row) => row.closedCount > 0 || row.openCount > 0)
                .sort((first, second) => second.totalPnl - first.totalPnl)
                .slice(0, 5),
        [strategies, trades, days, today]
    );

    const maxContribution = Math.max(1, ...contributors.map((row) => Math.abs(row.totalPnl)));
    const performanceHref = `/user/portfolioPerformance/${portfolioId}`;

    return (
        <div className="mb-4 rounded-lg bg-[#1a1a1a] text-white shadow-md">
            <div className="p-4">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                        <h5 className="text-xl font-medium">Performance at a glance</h5>
                        <div className="text-sm text-zinc-400">
                            Saved performance history, excluding funding changes. Today’s live result is shown above.
                        </div>
                    </div>
                    <Link className="text-sm text-blue-400 hover:text-blue-300" to={performanceHref}>
                        Open full performance →
                    </Link>
                </div>

                {breakdownQuery.isLoading ? (
                    <div className="text-sm text-zinc-500">Loading performance…</div>
                ) : breakdownQuery.error ? (
                    <div className="text-sm text-zinc-500">Performance summary unavailable right now.</div>
                ) : days.length === 0 ? (
                    <div className="text-sm text-zinc-500">No equity history yet. The summary appears after the first daily snapshot.</div>
                ) : (
                    <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)]">
                        <div className="rounded-lg border border-zinc-800 bg-[#121212] p-3">
                            <div className="text-xs uppercase tracking-wider text-zinc-400">Last {sparklineDays.length} days · equity</div>
                            <div className="mt-2">
                                {sparklineDays.length >= 2 ? (
                                    <Sparkline
                                        positive={sparklineDays[sparklineDays.length - 1].endingEquity >= sparklineDays[0].startingEquity}
                                        values={sparklineDays.map((day) => day.endingEquity)}
                                    />
                                ) : (
                                    <div className="text-sm text-zinc-500">Not enough days to chart.</div>
                                )}
                            </div>
                            <div className="mt-1 flex justify-between text-xs text-zinc-500">
                                <span>{formatShortDate(sparklineDays[0].date)}</span>
                                <span>{formatUnsignedCurrency(sparklineDays[sparklineDays.length - 1].endingEquity, 0)} latest</span>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                            {glanceStats.map((stat) => (
                                <Link
                                    className="rounded-md border border-zinc-800 bg-[#121212] p-2.5 hover:border-zinc-600"
                                    key={stat.label}
                                    to={performanceHref}
                                >
                                    <div className="text-[11px] text-zinc-400">{stat.label}</div>
                                    <div className={`mt-0.5 text-lg font-semibold ${signedToneClass(stat.pnl, 'text-white')}`}>
                                        {formatSignedCurrency(stat.pnl, 0)}
                                    </div>
                                    <div className={`text-xs ${signedToneClass(stat.returnPercent)}`}>
                                        {formatSignedPercent(stat.returnPercent)}
                                    </div>
                                </Link>
                            ))}
                        </div>

                        <div className="rounded-lg border border-zinc-800 bg-[#121212] p-3">
                            <div className="text-xs uppercase tracking-wider text-zinc-400">Contribution by strategy · all time</div>
                            {contributors.length ? (
                                <div className="mt-2 flex flex-col gap-2">
                                    {contributors.map((row) => {
                                        const width = Math.max(2, (Math.abs(row.totalPnl) / maxContribution) * 52);

                                        return (
                                            <Link
                                                className="grid grid-cols-[minmax(0,1fr)_110px_90px] items-center gap-2 text-sm hover:text-white"
                                                key={row.strategyId}
                                                title={`${row.name} (#${row.strategyId}) · realized ${formatSignedCurrency(row.realizedPnl, 0)} · open ${formatSignedCurrency(row.openPnl, 0)}`}
                                                to={`${performanceHref}?strategy=${row.strategyId}`}
                                            >
                                                <span className="flex min-w-0 items-center gap-1.5 text-zinc-300">
                                                    <StrategyColorDot color={strategyColors.get(row.strategyId) ?? '#71717a'} />
                                                    <span className="truncate">{row.name}</span>
                                                </span>
                                                <span className="inline-flex items-center">
                                                    <span className="flex w-[55px] justify-end">
                                                        {row.totalPnl < 0 && (
                                                            <span className="h-2.5 rounded-sm bg-red-500" style={{ width }} />
                                                        )}
                                                    </span>
                                                    <span className="flex w-[55px] justify-start border-l border-zinc-700">
                                                        {row.totalPnl > 0 && (
                                                            <span className="h-2.5 rounded-sm bg-[#29a740]" style={{ width }} />
                                                        )}
                                                    </span>
                                                </span>
                                                <span className={`text-right font-semibold ${signedToneClass(row.totalPnl)}`}>
                                                    {formatSignedCurrency(row.totalPnl, 0)}
                                                </span>
                                            </Link>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="mt-2 text-sm text-zinc-500">No trades yet.</div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PerformanceGlanceCard;
